import * as React from "react";
import cx from "classnames";

import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import { getTestAttributes } from "@common/utils/tests";
import { Loading } from "@common/components/loading";

import ActionsButton, { TRIGGER_TYPE } from "../ActionsButton";

import "./actions.styles.scss";

export type ActionsItemOption = { onClick: () => void; label: string; disabled?: boolean };

export type ActionsItem = {
  id: string;
  iconSrc?: string;
  label: React.ReactNode;
  onClick?: () => Promise<void> | void;
  disabled?: boolean;
  dropdownProps?: {
    classes?: {
      triggerBtn?: string;
      menuContainer?: string;
    };
    options?: Array<ActionsItemOption>;
    customDropdown?: React.ReactNode;
    useDefaultPosition?: boolean;
  };
};

export type ActionsProps = {
  items: Array<ActionsItem>;
  className?: string;
  classes?: {
    root?: string;
    item?: string;
  };
};

export const Actions: React.FC<ActionsProps> = (props) => {
  const { className, items, classes } = props;
  const [loadingBtnId, setLoadingBtnId] = React.useState<string | undefined>(undefined);

  const handleAction = React.useCallback(
    (fn: () => Promise<void> | void, id: string) => async () => {
      const result = fn();
      if (result && typeof (result as Promise<unknown>).then === "function") {
        setLoadingBtnId(id);
        try {
          await result;
        } finally {
          setLoadingBtnId(undefined);
        }
      }
    },
    []
  );

  const itemsLocal = items.map(({ onClick, ...restData }) => ({
    ...restData,
    onClick: onClick ? handleAction(onClick, restData.id) : undefined,
  }));

  const renderActionBtn = React.useCallback(({ item }: { item: ActionsItem }) => {
    const { iconSrc, label, ...restItem } = item;
    return (
      <Button {...restItem} className={cx("ButtonsActions__btn", classes?.item)}>
        {loadingBtnId !== restItem.id ? iconSrc && <img src={iconSrc} /> : <Loading size={16} />}
        {label}
      </Button>
    );
  }, []);

  return (
    <Flex
      className={cx(
        "ButtonsActions",
        {
          "ButtonsActions--disabled": loadingBtnId !== undefined,
        },
        className,
        classes?.root
      )}
      alignItems="center"
    >
      {itemsLocal.map((btnProps, actionIndex) => {
        const { id, dropdownProps, ...btnRestProps } = btnProps;

        if (dropdownProps?.options || dropdownProps?.customDropdown) {
          return (
            <ActionsButton
              disabled={btnProps.disabled}
              key={getTestAttributes({ id }).id}
              id={getTestAttributes({ id: `${id}-${actionIndex}` }).id}
              stopPropagationOnRoot
              menuItems={dropdownProps.options}
              classes={dropdownProps.classes}
              useDefaultPosition={dropdownProps.useDefaultPosition}
              triggerType={TRIGGER_TYPE.click}
              withBackdrop
              openOnClick
              trigger={
                <React.Fragment>
                  {renderActionBtn({
                    item: {
                      ...btnRestProps,
                      id: getTestAttributes({ id }).id as string,
                      label: (
                        <Flex justify="center" alignItems="center">
                          {btnProps.label}
                          <span className="caret" style={{ marginLeft: "4px" }}></span>
                        </Flex>
                      ),
                    },
                  })}
                </React.Fragment>
              }
            >
              {dropdownProps.customDropdown}
            </ActionsButton>
          );
        }

        return (
          <React.Fragment key={id}>
            {renderActionBtn({
              item: {
                ...btnRestProps,
                id: getTestAttributes({ id }).id as string,
              },
            })}
          </React.Fragment>
        );
      })}
    </Flex>
  );
};
